/**
 * The pixel logo: "เก้ากระบี่ออนไลน์" over "NINE SWORDS", drawn small (art pixels) with a hard-edged gold
 * fill in three bands, a dark outline and a drop shadow, between two planted swords; the canvas is scaled
 * by the stage's whole-number scale in CSS (image-rendering: pixelated), so it is crisp at any window size.
 */
const FONT = "600 26px Mitr, 'Noto Sans Thai', system-ui, sans-serif";
const EN = "700 11px 'Pixelify Sans', monospace";

/** Alpha snapped to on/off: text becomes hard pixels. */
function crisp(c: HTMLCanvasElement): void {
  const g = c.getContext('2d', { willReadFrequently: true })!;
  const img = g.getImageData(0, 0, c.width, c.height);
  for (let i = 3; i < img.data.length; i += 4) img.data[i] = img.data[i]! > 120 ? 255 : 0;
  g.putImageData(img, 0, 0);
}

function sword(g: CanvasRenderingContext2D, x: number, y: number): void {
  // A small upright sword, 5×34: blade, guard, grip, pommel.
  g.fillStyle = '#0f0c16';
  g.fillRect(x - 1, y - 1, 5, 28);
  g.fillStyle = '#9fb3cf';
  g.fillRect(x + 1, y, 1, 22);
  g.fillStyle = '#eef4ff';
  g.fillRect(x, y, 1, 22);
  g.fillStyle = '#d9b46c';
  g.fillRect(x - 3, y + 22, 9, 2);
  g.fillStyle = '#7c2620';
  g.fillRect(x, y + 24, 3, 5);
}

export async function drawLogo(canvas: HTMLCanvasElement, big = true): Promise<void> {
  try {
    await Promise.race([document.fonts.load(FONT, 'เก้า'), new Promise((r) => setTimeout(r, 1500))]);
    await Promise.race([document.fonts.load(EN, 'NINE'), new Promise((r) => setTimeout(r, 800))]);
  } catch {
    // fall back to system fonts
  }
  const TH = 'เก้ากระบี่ออนไลน์';
  const W = big ? 230 : 190;
  const H = 70;
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d', { willReadFrequently: true })!;
  g.clearRect(0, 0, W, H);
  // Title: outline ring, shadow, then the fill clipped from a banded gradient.
  const text = document.createElement('canvas');
  text.width = W;
  text.height = 44;
  const t = text.getContext('2d', { willReadFrequently: true })!;
  t.font = FONT;
  t.textAlign = 'center';
  t.textBaseline = 'alphabetic';
  const fit = Math.min(1, (W - 40) / t.measureText(TH).width);
  t.font = FONT.replace('26px', `${Math.floor(26 * fit)}px`);
  t.fillStyle = '#fff';
  t.fillText(TH, W / 2, 33);
  crisp(text);
  // Fill bands.
  const fill = document.createElement('canvas');
  fill.width = W;
  fill.height = 44;
  const f = fill.getContext('2d')!;
  f.drawImage(text, 0, 0);
  f.globalCompositeOperation = 'source-atop';
  const bands: [number, string][] = [[0, '#fff4c2'], [12, '#f6d777'], [20, '#e0a93f'], [28, '#b4782a']];
  bands.forEach(([y, c], i) => {
    f.fillStyle = c;
    f.fillRect(0, y, W, (bands[i + 1]?.[0] ?? 44) - y);
  });
  // Outline: the text offset in eight directions, dark.
  const ol = document.createElement('canvas');
  ol.width = W;
  ol.height = 44;
  const o = ol.getContext('2d')!;
  for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, 1], [-1, 1], [1, -1], [0, 2], [1, 2], [-1, 2]]) o.drawImage(text, dx, dy);
  o.globalCompositeOperation = 'source-in';
  o.fillStyle = '#1a1020';
  o.fillRect(0, 0, W, 44);
  const y0 = 4;
  // Shadow (maroon), 2 px down.
  g.globalAlpha = 1;
  g.save();
  g.translate(0, 2);
  g.drawImage(ol, 0, y0);
  g.globalCompositeOperation = 'source-atop';
  g.fillStyle = '#5a1a24';
  g.fillRect(0, 0, W, H);
  g.restore();
  g.drawImage(ol, 0, y0);
  g.drawImage(fill, 0, y0);
  // Swords and the latin line.
  sword(g, 8, 6);
  sword(g, W - 12, 6);
  g.font = EN;
  g.textAlign = 'center';
  const en = 'NINE SWORDS';
  const letters = [...en];
  const step = 11;
  const x0 = W / 2 - ((letters.length - 1) * step) / 2;
  letters.forEach((ch, i) => {
    g.fillStyle = '#1a1020';
    for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) g.fillText(ch, x0 + i * step + dx, 63 + dy);
    g.fillStyle = '#d9b46c';
    g.fillText(ch, x0 + i * step, 63);
  });
  // A rule with a diamond.
  g.fillStyle = '#8a6d45';
  g.fillRect(24, 51, W / 2 - 34, 1);
  g.fillRect(W / 2 + 10, 51, W / 2 - 34, 1);
  g.fillStyle = '#d9b46c';
  g.fillRect(W / 2 - 1, 49, 3, 5);
  g.fillRect(W / 2 - 3, 51, 7, 1);
  crisp(canvas);
}
